import { useState, useRef, useCallback, useEffect } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useApp } from '@/hooks/use-app-context';
import { useToast } from '@/hooks/use-toast';
import { useLanguages } from '@/hooks/use-languages';
import { getDefaultLanguages } from '@/lib/language.utils';
import { canAccessModeratorWorkspace } from '@/lib/auth/roles';
import { safePlay } from '@/lib/audio-playback';
import { validationApi } from '@/api/validation.api';
import { audioApi } from '@/api/audio.api';
import { useSessionDeepLink } from '@/hooks/use-session-deeplink';
import { useValidationGate, VALIDATION_GATE_QUERY_KEY } from '@/features/validation-gate';
import { useAudioWaveform } from '@/features/contribution-kit/use-audio-waveform';
import { mapLanguageToCode, extractErrorMessage } from './validate-audio.helpers';
import {
  VALIDATE_AUDIO_PAGE_SIZE,
  acceptPayload,
  applyCorrectedTexts,
  decisionCounts,
  markDecision,
  mergeValidationBatch,
  rejectPayload,
  removeAudioAt,
  undoDecisionOnLeave,
} from './validate-audio.session';
import type { NormalizedAudio, UploadTiming } from './types';

export function useValidateAudioSession() {
  const { t, user, language, theme } = useApp();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  const { activeLanguages } = useLanguages();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const validationGate = useValidationGate();

  const currentLang = lang || language || 'fr';
  const languages = activeLanguages.length > 0 ? activeLanguages : getDefaultLanguages();

  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [currentAudio, setCurrentAudio] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [detailedRejectionReason, setDetailedRejectionReason] = useState('');
  const [validatedAudios, setValidatedAudios] = useState<boolean[]>([]);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [totalValidations, setTotalValidations] = useState(0);
  const [pendingAudios, setPendingAudios] = useState<NormalizedAudio[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmedLanguage, setConfirmedLanguage] = useState('');
  const [showLanguageConfirmation, setShowLanguageConfirmation] = useState(false);
  const [audioGender, setAudioGender] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [currentAudioTiming, setCurrentAudioTiming] = useState<UploadTiming | null>(null);
  const [sessionComplete, setSessionComplete] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);

  const totalAudios = pendingAudios.length;
  const currentAudioData = pendingAudios[currentAudio] || null;
  const { accepted: acceptedCount, rejected: rejectedCount, decided: decidedCount } = decisionCounts(pendingAudios);
  const sessionProgress = totalAudios > 0 ? (decidedCount / totalAudios) * 100 : 0;
  const hasValidated = currentAudioData?.hasValidated ?? false;
  const hasRejected = currentAudioData?.hasRejected ?? false;

  useEffect(() => {
    if (currentAudioData?.audioGender) {
      setAudioGender(String(currentAudioData.audioGender));
    } else {
      setAudioGender('');
    }
    if (currentAudioData?.uploadTiming) {
      setCurrentAudioTiming(currentAudioData.uploadTiming);
    } else {
      setCurrentAudioTiming(null);
    }
    if (audioRef.current && currentAudioData?.audioUrl) {
      audioRef.current.load();
      setIsPlaying(false);
    }
  }, [currentAudioData]);

  // Waveform of the audio on screen (drawing and seek moved to the kit).
  const { canvasRef, peaks } = useAudioWaveform({ audioRef, audioUrl: currentAudioData?.audioUrl, theme });

  const loadPage = useCallback(
    async (page: number) => {
      setIsLoading(true);
      try {
        const langCode = mapLanguageToCode(selectedLanguage);

        const [pendingResult, acceptedResult] = await Promise.all([
          validationApi.listByLangAndStatus(langCode, 'pending', page, VALIDATE_AUDIO_PAGE_SIZE),
          validationApi.listByLangAndStatus(langCode, 'accepted', page, VALIDATE_AUDIO_PAGE_SIZE),
        ]);

        const allAudios = mergeValidationBatch(pendingResult, acceptedResult);

        if (allAudios.length) {
          setPendingAudios(allAudios);
          setCurrentAudio(0);
          setSessionStarted(true);
          setPageNumber(page);
          toast({
            title: String(t.audioBatchLoaded ?? ''),
            description: String(t.audioBatchLoadedDesc ?? '')
              .replace('{page}', String(page))
              .replace('{count}', String(allAudios.length)),
          });
        } else {
          if (page === 1) {
            toast({ title: String(t.noAudioToValidate ?? ''), description: String(t.noAudioToValidateDesc ?? '') });
          } else {
            setSessionComplete(true);
          }
        }
      } catch (error: unknown) {
        const description = extractErrorMessage(error) ?? String(t.audioLoadFailed ?? '');
        toast({ title: String(t.error ?? ''), description, variant: 'destructive' });
      } finally {
        setIsLoading(false);
      }
    },
    [selectedLanguage, t, toast],
  );

  const startSession = useCallback(async () => {
    if (!selectedLanguage) {
      toast({ title: String(t.languageRequiredTitle ?? ''), description: String(t.languageRequiredDesc ?? ''), variant: 'destructive' });
      return;
    }
    setPageNumber(1);
    setIsLoading(true);
    setCurrentAudio(0);
    try {
      await loadPage(1);
    } finally {
      setIsLoading(false);
    }
  }, [selectedLanguage, t, toast, loadPage]);

  const { startLinkedSession } = useSessionDeepLink({
    searchParams,
    setSearchParams,
    sessionValue: 'validate-audio',
    selectedLanguage,
    setSelectedLanguage,
    sessionStarted,
    isStartingSession: isLoading,
    startSession,
  });

  const playAudio = useCallback(() => {
    const element = audioRef.current;
    if (!element) return;
    if (isPlaying) {
      element.pause();
      setIsPlaying(false);
      return;
    }
    setIsPlaying(true);
    safePlay(element, { onError: () => setIsPlaying(false) });
  }, [isPlaying]);

  const handleAudioEnded = useCallback(() => setIsPlaying(false), []);

  const moveToNext = useCallback(async () => {
    if (currentAudio < totalAudios - 1) {
      setCurrentAudio((prev) => prev + 1);
      setRejectionReason('');
      setAudioGender('');
    } else {
      await loadPage(pageNumber + 1);
    }
  }, [currentAudio, totalAudios, pageNumber, loadPage]);

  const previousAudio = useCallback(() => {
    if (currentAudio > 0) {
      const audioToUnvalidate = currentAudio;
      setCurrentAudio((prev) => prev - 1);
      setRejectionReason('');
      setAudioGender('');
      
      // Annuler la validation de l'audio qu'on quitte (affichage seulement)
      if (undoDecisionOnLeave(pendingAudios, audioToUnvalidate).undone) {
        setPendingAudios((prev) => undoDecisionOnLeave(prev, audioToUnvalidate).audios);
        setTotalValidations((prev) => Math.max(0, prev - 1));
      }
    }
  }, [currentAudio, pendingAudios]);

  const acceptAudio = useCallback(async () => {
    if (!currentAudioData) return;
    if (!audioGender.trim()) {
      toast({ title: String(t.genderRequired ?? ''), description: String(t.genderRequiredDesc ?? ''), variant: 'destructive' });
      return;
    }

    // Empêcher double-clic
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (!user?.id) throw new Error('User required');
      await validationApi.validateAudio(acceptPayload(currentAudioData.id, audioGender));
      void queryClient.invalidateQueries({ queryKey: VALIDATION_GATE_QUERY_KEY });

      setPendingAudios((prev) => markDecision(prev, currentAudio, String(user.id), 'accept'));
      setTotalValidations((prev) => prev + 1);
      
      // Afficher le toast avant de passer à l'audio suivant
      toast({
        title: String(t.audioAccepted ?? ''),
        description: currentAudioTiming
          ? String(t.audioAcceptedWithDuration ?? '').replace('{duration}', currentAudioTiming.durationSeconds)
          : String(t.audioAcceptedDesc ?? ''),
      });
      
      // Attendre le prochain cycle pour laisser les setState prendre effet
      requestAnimationFrame(() => {
        moveToNext();
      });
    } catch (error) {
      const description = extractErrorMessage(error) ?? String(t.audioValidationFailed ?? '');
      toast({ title: String(t.error ?? ''), description, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  }, [currentAudioData, audioGender, user?.id, currentAudio, currentAudioTiming, t, toast, moveToNext, isSubmitting, queryClient]);

  const rejectAudio = useCallback(async () => {
    if (!currentAudioData || !rejectionReason.trim()) {
      toast({ title: String(t.reasonRequired ?? ''), description: String(t.reasonRequiredDesc ?? ''), variant: 'destructive' });
      return;
    }

    // Empêcher double-clic
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (!user?.id) throw new Error('User required');
      await validationApi.validateAudio(rejectPayload(currentAudioData.id, rejectionReason, detailedRejectionReason));
      void queryClient.invalidateQueries({ queryKey: VALIDATION_GATE_QUERY_KEY });

      setPendingAudios((prev) => markDecision(prev, currentAudio, String(user.id), 'reject'));
      setTotalValidations((prev) => prev + 1);
      
      // Afficher le toast avant de passer à l'audio suivant
      toast({
        title: String(t.audioRejected ?? ''),
        description: currentAudioTiming
          ? String(t.audioRejectedWithDuration ?? '')
              .replace('{reason}', rejectionReason)
              .replace('{duration}', currentAudioTiming.durationSeconds)
          : String(t.audioRejectedDesc ?? '').replace('{reason}', rejectionReason),
      });
      
      setRejectionReason('');
      
      // Attendre le prochain cycle pour laisser les setState prendre effet
      requestAnimationFrame(() => {
        moveToNext();
      });
    } catch (error) {
      const description = extractErrorMessage(error) ?? String(t.audioRejectFailed ?? '');
      toast({ title: String(t.error ?? ''), description, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  }, [currentAudioData, rejectionReason, detailedRejectionReason, user?.id, currentAudio, currentAudioTiming, t, toast, moveToNext, isSubmitting, queryClient]);

  const deleteCurrentAudio = useCallback(async () => {
    if (!currentAudioData?.blobName) {
      toast({ title: String(t.error ?? ''), description: String(t.audioDeleteFailed ?? ''), variant: 'destructive' });
      return;
    }
    if (isSubmitting || isDeleting) return;

    setIsDeleting(true);
    try {
      await audioApi.deleteAudio(currentAudioData.blobName);

      setPendingAudios((prev) => {
        const next = removeAudioAt(prev, currentAudio, currentAudio);
        setCurrentAudio(next.cursor);
        return next.audios;
      });

      toast({
        title: String(t.audioDeleted ?? ''),
        description: String(t.audioDeletedDescription ?? ''),
      });
    } catch (error) {
      const description = extractErrorMessage(error) ?? String(t.audioDeleteFailed ?? '');
      toast({ title: String(t.error ?? ''), description, variant: 'destructive' });
    } finally {
      setIsDeleting(false);
    }
  }, [currentAudioData, currentAudio, isSubmitting, isDeleting, t, toast]);

  // Spec 003: show a moderator's correction in place, without reloading the batch.
  const applyAudioTexts = useCallback((audioId: string, texts: { phraseLocal: string | null; phraseFr: string | null }) => {
    setPendingAudios((prev) => applyCorrectedTexts(prev, audioId, texts));
  }, []);

  const restartValidation = useCallback(() => {
    setSessionComplete(false);
    setCurrentAudio(0);
    setValidatedAudios([]);
    setTotalValidations(0);
    setPendingAudios([]);
    setSessionStarted(false);
    setRejectionReason('');
    setAudioGender('');
  }, []);

  // A moderator validates from their own space: "back" returns there, not to
  // the public home page they cannot use.
  const isModerator = canAccessModeratorWorkspace(user?.role);
  const goHome = useCallback(
    () => navigate(isModerator ? `/${currentLang}/moderator/todo` : `/${currentLang}`),
    [currentLang, isModerator, navigate],
  );

  const navigateToReRecord = useCallback(() => {
    const currentPhrase = (currentAudioData?.word || '').trim();
    if (currentPhrase) {
      navigate(`/${currentLang}/speak-contribute`, {
        state: {
          reRecordPhrase: currentPhrase,
          fromValidation: true,
          rejectionReason,
          detailedReason: detailedRejectionReason,
        },
      });
    }
  }, [currentAudioData, currentLang, navigate, rejectionReason, detailedRejectionReason]);

  return {
    t,
    user,
    currentLang,
    languages,
    selectedLanguage,
    setSelectedLanguage,
    sessionStarted,
    sessionComplete,
    isLoading,
    isSubmitting,
    isDeleting,
    currentAudio,
    totalAudios,
    currentAudioData,
    sessionProgress,
    acceptedCount,
    rejectedCount,
    totalValidations,
    validatedAudios,
    isPlaying,
    audioRef,
    canvasRef,
    peaks,
    audioGender,
    setAudioGender,
    rejectionReason,
    setRejectionReason,
    detailedRejectionReason,
    setDetailedRejectionReason,
    confirmedLanguage,
    setConfirmedLanguage,
    showLanguageConfirmation,
    setShowLanguageConfirmation,
    hasValidated,
    hasRejected,
    startLinkedSession,
    playAudio,
    handleAudioEnded,
    acceptAudio,
    rejectAudio,
    deleteCurrentAudio,
    applyAudioTexts,
    previousAudio,
    moveToNext,
    goHome,
    restartValidation,
    navigateToReRecord,
    pendingAudios,
    validationGate,
  };
}
